/**
 * KycCheckboxGroup - 多选框组件
 *
 * @description KYC表单专用的多选框组件，支持多个选项同时选中，并支持自定义输入
 * @features
 * - 支持多选功能，value为数组类型
 * - 集成AsFormItemLayout，提供完整的表单项布局
 * - 支持表单验证和错误提示显示
 * - 支持自定义题目编号显示
 * - 基于CommonCheckbox实现，保持一致的交互体验
 * - 支持自定义输入选项（当选项标题为"Other"时）
 *
 * @usage
 * ```tsx
 * // 基础多选用法
 * <KycCheckboxGroup
 *   value={["option1", "option3"]}
 *   label="请选择您的投资偏好"
 *   sequenceNumber="1"
 *   options={[
 *     {value: 'option1', title: 'option1'},
 *     {value: 'option2', title: 'option2'},
 *     {value: 'option3', title: 'option3'},
 *     {value: 'other', title: 'Other'}
 *   ]}
 *   onValueChange={(values) => console.log('选中项:', values)}
 * />
 * ```
 *
 * @note 此组件专为KYC表单设计，特别适用于问卷调查和多选场景
 */

import React, { memo, useCallback, useEffect, useState } from "react";
import { StyleSheet, View } from "../platformComponents";
import type { IOptionItem, TKycCheckboxGroupProps } from "@adgm/cross-models";
import {
  colors,
  flexDirection,
  fontFamily,
  fontSize,
  height,
  lineHeight,
  margin,
  padding,
  width,
} from "../styles/common";
import { AsFormItemLayout } from "./AsFormItemLayout";
import { i18nCrossUtil, IS_WEB, tranformPx } from "@adgm/libs";
import { CommonCheckbox, CommonInput } from "../common";
import { CUSTOM_RADIO_WITH_INPUT } from "@adgm/cross-config";
import { KycLabelElement } from "./KycLabelElement";
import { isEqual } from "lodash-es";
import { useGenericFormContext } from "./GenericForm";

/**
 * 自定义比较函数，用于优化memo性能
 * @description 对value数组进行深度比较，其他props使用浅比较
 * @param prevProps - 前一次props
 * @param nextProps - 下一次props
 * @returns boolean - 是否相等
 */
const areEqual = (prevProps: TKycCheckboxGroupProps, nextProps: TKycCheckboxGroupProps) => {
  return (
    isEqual(prevProps.value, nextProps.value) &&
    prevProps.label === nextProps.label &&
    prevProps.error === nextProps.error &&
    prevProps.sequenceNumber === nextProps.sequenceNumber &&
    prevProps.tip === nextProps.tip &&
    prevProps.hideErrorSpace === nextProps.hideErrorSpace &&
    prevProps.required === nextProps.required &&
    prevProps.options === nextProps.options &&
    prevProps.style === nextProps.style
  );
};

/**
 * KycCheckboxGroup组件主实现
 *
 * @description KYC表单多选框的实现，支持多个选项同时选中，并支持自定义输入
 * @param props - 组件属性，详见TKycCheckboxGroupProps接口
 * @returns React函数组件，渲染完整的多选框表单项
 */
export const KycCheckboxGroup = memo((props: TKycCheckboxGroupProps) => {
  const {
    value = [],
    label,
    error,
    options,
    style,
    sequenceNumber,
    tip,
    hideErrorSpace,
    onValueChange,
    required,
    ...restProps
  } = props;
  const [customInputValues, setCustomInputValues] = useState<Record<string, string>>({});
  const crossIntl = i18nCrossUtil.t();
  const { genericFormType } = useGenericFormContext();
  const isQuestionnaireForm = genericFormType === "questionnaire";
  useEffect(() => {
    if (!value || value.length === 0) {
      setCustomInputValues({});
      return;
    }

    const newCustomInputValues: Record<string, string> = {};

    // 遍历所有选项，找出自定义输入选项
    options.forEach(option => {
      if (option.value === CUSTOM_RADIO_WITH_INPUT) {
        // 如果value中包含该选项值，说明选中了但没有输入
        if (value.includes(option.value)) {
          newCustomInputValues[option.value] = "";
        } else {
          // 检查是否有不属于其他标准选项的值（即自定义输入值）
          const customValue = value.find(v => !options.some(opt => opt.value === v));
          if (customValue) {
            newCustomInputValues[option.value] = customValue;
          }
        }
      }
    });

    setCustomInputValues(newCustomInputValues);
  }, [value, options]);

  /**
   * 处理单个选项的选择/取消选择
   * @description 当用户点击某个选项时触发，添加或移除该选项
   * @param optionValue - 被操作的选项值
   */
  const handleItemChange = useCallback(
    (optionValue: string) => {
      if (!onValueChange) return;

      const option = options.find(opt => opt.value === optionValue);
      const isCurrentlySelected = value.includes(optionValue);

      // 检查是否有对应的自定义输入值
      const customValue = customInputValues[optionValue];
      const hasCustomInput = customValue && customValue.trim();

      let newValues: string[];

      if (isCurrentlySelected || hasCustomInput) {
        // 如果已选中或有自定义输入，则移除
        newValues = value.filter(item => item !== optionValue);

        // 如果是自定义输入选项，还需要移除自定义输入值
        if (option?.value === CUSTOM_RADIO_WITH_INPUT && hasCustomInput) {
          newValues = newValues.filter(v => v !== customValue);
        }

        // 清除自定义输入状态
        if (option?.value === CUSTOM_RADIO_WITH_INPUT) {
          setCustomInputValues(prev => {
            const { [optionValue]: _, ...rest } = prev;
            return rest;
          });
        }
      } else {
        newValues = [...value, optionValue];
        if (option?.value === CUSTOM_RADIO_WITH_INPUT) {
          setCustomInputValues(prev => ({
            ...prev,
            [optionValue]: "",
          }));
        }
      }

      onValueChange(newValues);
    },
    [value, onValueChange, customInputValues, options],
  );

  /**
   * 处理自定义输入值变化
   * @param optionValue - 对应的选项值
   * @param inputValue - 用户输入的值
   */
  const handleCustomInputChange = useCallback(
    (optionValue: string, inputValue: string) => {
      setCustomInputValues(prev => ({
        ...prev,
        [optionValue]: inputValue,
      }));

      let newValues = [...value];

      // 移除旧的自定义输入值和选项值
      const oldCustomValue = customInputValues[optionValue];
      if (oldCustomValue) {
        newValues = newValues.filter(v => v !== oldCustomValue);
      }
      newValues = newValues.filter(v => v !== optionValue);

      if (inputValue.trim()) {
        // 有输入内容时，添加输入值
        newValues.push(inputValue);
      } else {
        // 无输入内容时，添加选项值（保持选中状态）
        newValues.push(optionValue);
      }

      onValueChange?.(newValues);
    },
    [value, customInputValues, onValueChange],
  );

  const renderCustomChild = (item: IOptionItem) => {
    if (item.value === CUSTOM_RADIO_WITH_INPUT) {
      return (
        <CommonInput
          style={styles.customInput}
          value={customInputValues[item.value] || ""}
          onValueChange={inputValue => handleCustomInputChange(item.value, inputValue)}
          placeholderTextColor={IS_WEB ? colors.white21 : colors.white14}
          placeholder={crossIntl["kyc.other.input"]}
          trim={false}
        />
      );
    }

    return null;
  };

  return (
    <AsFormItemLayout
      {...restProps}
      required={required}
      error={error}
      tip={tip}
      style={[style, styles.label]}
      hideErrorSpace={hideErrorSpace}
      containerStyle={{
        backgroundColor: colors.transparent,
      }}
      labelElement={
        <KycLabelElement label={label} sequenceNumber={sequenceNumber} required={required} />
      }>
      <View style={[styles.container, { paddingLeft: isQuestionnaireForm ? padding.p40 : 0 }]}>
        {options.map((item, index) => {
          let isChecked = value.includes(item.value);

          // 对于自定义输入选项，还需要检查是否有自定义输入值在value数组中
          if (item.value === CUSTOM_RADIO_WITH_INPUT) {
            const customValue = customInputValues[item.value];
            if (customValue && customValue.trim()) {
              isChecked = value.includes(customValue);
            }
          }

          return (
            <View
              style={index === options.length - 1 ? {} : styles.optionContainer}
              key={`${item.value}-${index}`}>
              <CommonCheckbox
                value={item.value}
                checked={isChecked}
                label={item.title}
                onChange={handleItemChange}>
                {renderCustomChild(item)}
              </CommonCheckbox>
            </View>
          );
        })}
      </View>
    </AsFormItemLayout>
  );
}, areEqual);

/**
 * 组件样式定义
 * @description 定义多选框容器、标签等的样式
 */
const styles = StyleSheet.create({
  /** 选项容器样式 */
  container: {
    flex: 1,
  },
  optionContainer: {
    paddingBottom: padding.p24,
  },
  item: {
    flexDirection: flexDirection.row,
  },
  text: {
    fontSize: fontSize.f14,
    lineHeight: lineHeight.l18,
    flex: 1,
  },
  icon: {
    width: width.w14,
    height: height.h14,
    marginTop: tranformPx(2.5),
    marginRight: margin.m13,
  },
  label: {
    fontSize: IS_WEB ? fontSize.f20 : fontSize.f16,
    fontFamily: IS_WEB ? "Common-Medium" : fontFamily.commonRegular,
  },
  customInput: {
    fontSize: fontSize.f14,
    flex: 1,
    fontFamily: IS_WEB ? "Common-Regular" : fontFamily.commonRegular,
    paddingVertical: IS_WEB ? padding.p14 : padding.p16,
    paddingHorizontal: IS_WEB ? padding.p14 : padding.p16,
    marginTop: padding.p8,
    backgroundColor: IS_WEB ? colors.grey22 : colors.mainBackground,
  },
});
